
import {React, useState, useContext, useEffect, useRef} from "react"
import {useStyle} from '../../context/styleContext/styleContext'
import ReactGA from 'react-ga4';
import { Paper } from '@mui/material';
import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";

export default function EQLearning(){

  const { style, setStyle } = useStyle();
  const [width, setWidth] = useState(window.innerWidth)

function handleWindowSizeChange() {
    setWidth(window.innerWidth);
}
useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
        window.removeEventListener('resize', handleWindowSizeChange);
    }
}, []);

const isMobile = width <= 768;

useEffect(() => {
  document.title = 'Parish Audio | Cutting-Edge Audio Processing | Equalization';
}, []);

ReactGA.send({
    hitType:"pageview",
    page:"learning/Equalization",
    title:"learning/Equalization",
  })

  const [height, setHeight] = useState(window.innerHeight)

  useEffect(() => {
    function handleResize() {      
      setWidth(window.innerWidth)
      setHeight(window.innerHeight)
  }
  
    window.addEventListener('resize', handleResize)
  })

const topContainer = useRef();
    useEffect(() => {
        // To make sure page starts from the top
        topContainer.current.scrollIntoView({ block: "end" });
    }, []);




    return(
    <><div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

    <h1 style={{marginTop:'25px'}} ref={topContainer} >Equalization</h1>
    <Divider className="mobile-card-divider" />
        <p>Equalization, commonly referred to as EQ, is the process of adjusting the balance of frequency components within an audio signal. It is commonly used to enhance sonic clarity, fix problem areas in a mix, or creatively shape a sound. EQ is one of the most essential tools in both live sound and studio production. Every sound we hear is made up of a range of frequencies, and EQ gives us the power to boost or cut specific ranges to mold that sound into something more pleasing, functional, or fitting in a mix.</p>
        <p>At its core, EQ allows for selective control of frequency bands. This means that different frequencies in your music can be boosted or cut, changing the sound. This can be done with specific frequencies, or wider bands, such as "low", "mid", and "high". These changes can be subtle or drastic, depending on the goal. From eliminating unwanted rumble and hiss to emphasizing the attack of a snare drum or softening the harshness of a vocal, EQ functions as a frequency-specific volume control.</p>
        <div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'400px', textAlign:'left'}}>

        <WikiCommonsImage width="100%" bottomText="Yamaha EQ-500 Graphic Equalizer" bottomTextLinkUrl="https://www.yamaha.com/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Yamaha_EQ-500_Graphic_Equalizer.jpg" imageSource="https://upload.wikimedia.org/wikipedia/commons/f/fa/Yamaha_EQ-500_Graphic_Equalizer.jpg" alt = "Yamaha EQ-500 Graphic Equalizer"  title = "Touho_T, CC BY 2.0 <https://creativecommons.org/licenses/by/2.0>, via Wikimedia Commons" />
</div>
        <h2 style={{marginTop:'30px'}}>A Brief History of EQ</h2>
        <p>The roots of EQ go back to the early days of telephony, when engineers needed to compensate for frequency loss over long-distance lines. This was done with passive filters that adjusted the frequency response. The term "equalization" referred to the goal of making the signal equally strong across all frequencies—a flat response.</p>
        <p>As audio equipment evolved, so did the application of EQ. In the 1920s, John Volkman of <a href="https://www.rca.com/" target="_blank" rel="noopener noreferrer">RCA</a> developed variable EQ for use in cinema sound systems. By the 1950s and 60s, pioneers like Art Davis and Daniel Flickinger were designing more sophisticated systems, including the first parametric equalizers, which allowed for adjustable frequency selection, gain, and bandwidth.</p>
        <p>The 1970s saw the rise of graphic equalizers, with their now-familiar rows of vertical sliders. These were commonly used in studios and live sound reinforcement, enabling engineers to visually sculpt sound across multiple frequency bands. In the digital era, parametric EQs have become more precise, accessible, and flexible than ever—especially through DAW plugins.</p>
        <h2 style={{marginTop:'30px'}}>How EQ Works</h2>
        <p>o understand how EQ works, it's helpful to think in terms of filters. A filter is a circuit or algorithm that boosts or attenuates frequencies. The main types of EQ filters include:</p>
        <ul class="a">
            <li><strong>High-pass filter (Low-cut): </strong>Removes low frequencies below a set threshold.</li>
            <li><strong>Low-pass filter (High-cut): </strong>Removes high frequencies above a set threshold.</li>
            <li><strong>Shelving filters: </strong>Boost or cut frequencies above (high shelf) or below (low shelf) a certain point.</li>
            <li><strong>Bell (peak/dip) filters: </strong>Boost or cut around a center frequency, with a controllable bandwidth.</li>
        </ul>
        <h2 style={{marginTop:'30px'}}>EQ Parameters</h2>
        <p>Modern EQ units—whether analog or digital—typically offer several adjustable parameters:</p>
        <ul class="a">
            <li><strong>Frequency: </strong>The center frequency of the range being adjusted.</li>
            <li><strong>Gain: </strong>The amount of boost or cut applied to the selected frequency range.</li>
            <li><strong>Q (Quality factor): </strong>Determines the width of the affected frequency band. Higher Q values affect narrower ranges.</li>
        </ul>
        <p>Understanding these controls allows engineers to perform precise adjustments, which is especially critical in complex mixes.</p>
        <h2 style={{marginTop:'30px'}}>Types of Equalizers</h2>
        <h3>Graphic Equalizer</h3>
        <p>Graphic EQs feature a fixed set of frequency bands, each with a dedicated slider. Moving a slider boosts or cuts that band, and the position of all the sliders together forms a visual "graph" of the EQ curve. Common configurations include 15-band (2/3 octave) and 31-band (1/3 octave) models, often used in live sound and venue tuning.</p>
        <h3>Parametric Equalizer</h3>
        <p>Parametric EQs allow complete control over each band: the center frequency, the Q factor, and the gain. These are the most flexible type of EQ and are widely used in mixing and mastering. Semi-parametric EQs offer adjustable frequency and gain but fixed Q.</p>
        <h3>Shelving Equalizer</h3>
        <p>Shelving EQs adjust frequencies above or below a certain point by a fixed amount. These are commonly seen in consumer audio gear as "bass" and "treble" controls.</p>
        <h2 style={{marginTop:'30px'}}>EQ by Frequency Range</h2>
        <p>EQ decisions often correspond to general frequency bands:</p>
        <ul class="a">
            <li><strong>Sub Bass (20–60 Hz): </strong>Adds physical power, but too much can overwhelm.</li>
            <li><strong>Bass (60–250 Hz):  </strong>The foundation of the mix; warmth and fullness.</li>
            <li><strong>Low Mids (250–500 Hz): </strong>Can add body or muddiness.</li>
            <li><strong>Mids (500 Hz–2 kHz): </strong>Where much of the musical detail resides.</li>
            <li><strong>High Mids (2–4 kHz): </strong>Presence and definition; can become harsh if overboosted.</li>
            <li><strong>Presence (4–7 kHz): </strong>Adds clarity and articulation.</li>
            <li><strong>Brilliance (7–20 kHz):  </strong>Airiness and sparkle; excessive boosting here introduces hiss.</li>
        </ul>
        <p>These ranges aren’t strict boundaries but serve as guidelines for navigating EQ.</p>
        <h2 style={{marginTop:'30px'}}>Applications in Music Production</h2>
        <p>EQ is used across all stages of music production:</p>
        <ul class="a">
            <li><strong>Tracking: </strong>Cutting low-end rumble from mics, shaping instrument tone.</li>
            <li><strong>Mixing: </strong>Carving space for each element, avoiding frequency clashes.</li>
            <li><strong>Mastering: </strong>Making broad tonal adjustments to the final stereo mix.</li>
        </ul>
        <p>One powerful use of EQ is "mirrored EQ"—where complementary instruments are cut and boosted in opposing ranges to reduce masking. For example, boosting bass guitar at 80 Hz while cutting the kick drum there, and reversing the process at 100 Hz, can help both elements coexist clearly.</p>
        <h2 style={{marginTop:'30px'}}>Live Sound and Feedback Control</h2>
        <p>EQ is also vital in live settings, particularly for managing feedback. A narrow cut with a high-Q parametric EQ can notch out a problematic frequency without affecting the overall tone. Graphic EQs are often used to flatten the room response or tame resonant peaks in a venue.</p>
        <p>Room tuning with a graphic EQ involves playing pink noise and analyzing the response with a spectrum analyzer. Boosts and cuts are then made to compensate for acoustic anomalies.</p>
        <h2 style={{marginTop:'30px'}}>Corrective vs. Creative EQ</h2>
        <p>EQ can be both a corrective tool and a creative one:</p>
        <ul class="a">
            <li><strong>Corrective EQ: </strong>Used to fix issues, like removing hiss, rumble, or nasal vocal resonances.</li>
            <li><strong>Creative EQ: </strong>Used to shape the tone for aesthetic purposes—adding warmth, clarity, or sparkle.</li>
        </ul>
        <p>For example, a gentle high-shelf boost can add air to a vocal, while a wide cut around 300 Hz might reduce muddiness in a mix.</p>
        <h2 style={{marginTop:'30px'}}>EQ in Practice: Tips and Techniques</h2>
        <h3>Use in Context</h3>
        <p>Always EQ a sound in the context of the full mix. A soloed instrument might sound thin after EQing, but in the mix it fits perfectly. EQing in isolation can lead to over-processing.</p>
        <h3>Cut Before You Boost</h3>
        <p>It’s often more transparent to reduce unwanted frequencies than to boost desired ones. This avoids adding unnecessary gain and reduces potential phase issues.</p>
        <h3>Sweep and Cut</h3>
        <p>To find unpleasant resonances, create a narrow boost with a high Q, sweep it across the spectrum, and cut where the sound is harsh or boomy.</p>
        <h3>Low-Cut Non-Bass Elements</h3>
        <p>Many instruments contain unnecessary low-end energy. High-pass filters can clean up pads, vocals, guitars, and FX to make room for bass and kick.</p>
        <h3>Use Automation</h3>
        <p>Sometimes a static EQ setting isn’t enough. Automating EQ settings allows dynamic tonal changes during a song—especially useful during transitions or builds.</p>
        <h2 style={{marginTop:'30px'}}>Pitfalls to Avoid</h2>
        <h3>Overuse</h3>
        <p>Applying too much EQ can lead to unnatural, harsh, or overly thin sounds. When multiple EQ bands are in use, cumulative phase shift can degrade clarity.</p>
        <h3>Chasing Presets</h3>
        <p>EQ presets can serve as starting points, but every mix is different. Relying on a "Vocal Boost" preset may not suit your unique vocal recording.</p>
        <h3>Boosting Frequencies That Aren’t There</h3>
        <p>If a sound lacks presence, boosting high mids won’t help if the frequencies don’t exist in the recording. EQ can't add what isn’t there.</p>
        <h3>Ignoring the Bigger Picture</h3>
        <p>A sound may shine on its own, but if it fights other instruments in the mix, it can muddy the whole track. EQ should serve the song, not individual sounds.</p>
        <h2 style={{marginTop:'30px'}}>Specialized EQ Uses</h2>
        <ul class="a">
        <li><strong>Rumble Filters: </strong>High-pass filters to remove subsonic noise from turntables or poor recordings.</li>
        <li><strong>Notch Filters: </strong>Ultra-narrow cuts used for surgical removal of hum or feedback.</li>
        <li><strong>Crossover EQ: </strong>Used in speaker systems to split frequencies between woofers and tweeters.</li>
        <li><strong>De-essing: </strong>A form of dynamic EQ targeting harsh sibilant sounds around 4–8 kHz.</li>
        </ul>
        <h2 style={{marginTop:'30px'}}>EQ in Hardware and Software</h2>
        <p>EQ can be found in:</p>
        <ul class="a">
            <li><strong>Mixing Consoles: </strong>Usually semi-parametric, often with shelving high and low bands.</li>
            <li><strong>Hardware EQs: </strong>Analog outboard gear like Pultec-style tube EQs add warmth and coloration.</li>
            <li><strong>Plugins: </strong>Digital EQs in DAWs offer graphical interfaces and surgical precision.</li>
            <li><strong>Multiband EQs: </strong>Offer separate control for frequency bands, often used in mastering.</li>
        </ul>

        <p>Some analog EQs are prized for their musicality and phase characteristics. Pultec EQs, for example, allow simultaneous boosting and cutting at the same frequency for a unique tonal shape.</p>
        <h2 style={{marginTop:'30px'}}>The Subtle Art of EQ</h2>
        <p>While EQ is often thought of as a technical tool, it’s ultimately about feel and expression. Great EQ decisions come not just from charts or visual graphs, but from listening deeply to how frequencies interact and using that knowledge to bring a track to life. Whether you're chasing clarity, warmth, punch, or air, EQ remains one of the most powerful tools in the audio engineer's toolkit. Use it with intention, trust your ears, and let it serve the music.</p>







    <br />
    <br />
    <br />
        <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
      </div>
       </>

    )
}

